import React, { useCallback, useEffect, useMemo, useRef } from "react";

import type { Handler } from "@use-gesture/react";
import { useDashboardContext } from "../../../../contexts/dashboard-context";
import { useClient } from "../../../../hooks/useClient";
import { useSelectedZone } from "../../../../hooks/useSelection";
import { IPanelSymbol, ISymbolsListPanelRef, SymbolsListPanel } from "./symbols-list-panel";
import { useSymbols } from "../../../../hooks/useSymbols";
import { useSelectedSymbol } from "./use-selected-symbol";
import { DragImage, IDragImageRef } from "../../../drag-image";
import { usePreviewContext } from "../../../../contexts/preview-context";
import { getCenterOfElement } from "../../../../utils/dom";

export function SymbolsPanelContainer() {
	const dashboard = useDashboardContext();
	const selectedZone = useSelectedZone();
	const { data: symbols } = useSymbols();
	const client = useClient();
	const currentSymbol = useSelectedSymbol(selectedZone);

	const { getZonesAtPoint, previewOverlayRef, getCenterOfZone } = usePreviewContext();

	const panelRef = useRef<ISymbolsListPanelRef>(null);

	const dragImageRef = useRef<IDragImageRef>(null);

	const lastHighlightedZoneRef = useRef<eXpress.core.IZone | undefined>(undefined);

	const onSymbolClick = useCallback(
		(code: string) => {
			const newSymbol = symbols?.find((sym) => sym.code === code);
			if (!newSymbol) return;
			if (newSymbol === currentSymbol) return;
			if (dashboard.canReplaceSymbol(selectedZone, false)) {
				// Replace Symbol
				dashboard.replaceSymbol(selectedZone, newSymbol, false);
			} else if (dashboard.canAddSymbolZone(selectedZone?.page)) {
				// Add Symbol
				dashboard.addSymbolZone({
					symbol: newSymbol,
					page: selectedZone?.page, // If no zone selected the default page is used
				});
			}
			// Else: panel was shown but user couldn't add/replace symbols,
		},
		[currentSymbol, dashboard, selectedZone, symbols],
	);

	const onDrag = useCallback<Handler<"drag">>(
		({ last, args, xy: [x, y], axis, tap, dragging, event, first }) => {
			const [symbol] = (args as IPanelSymbol[]) || [];

			if (!symbol) return;

			// ignore first event because we need to wait for a possible tap
			if (first) return;

			// tap happened
			if (tap) {
				onSymbolClick(symbol.code);
				return;
			}

			const lastHighlightedZone = lastHighlightedZoneRef?.current;

			// regular drag event
			if (axis === "y" && dragging) {
				// drag hasnt been started yet
				if (!dragImageRef?.current?.isDragging()) {
					const thumbUrl = symbol.url;
					if (thumbUrl) {
						const dragImageEl = event?.target as unknown as HTMLImageElement;
						if (!dragImageEl.classList.contains("symbols-list-panel__img")) {
							return;
						}
						const center = getCenterOfElement(dragImageEl);
						if (center) {
							dragImageRef?.current?.start(
								thumbUrl,
								x,
								y,
								center.left - x,
								center.top - y,
							);
						}
					}
				} else {
					// just dragging, move drag
					dragImageRef?.current?.moveTo(x, y);

					const validDropZone = getZonesAtPoint(
						{ x, y },
						(zone) =>
							eXpress.core.isSymbolZone(zone) &&
							dashboard.canReplaceSymbol(zone, true),
					).last() as eXpress.core.PhotoZone | undefined;

					// highlight target if hovering
					if (validDropZone !== lastHighlightedZone) {
						previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
							highlightZone: false,
						});
						previewOverlayRef.current?.updateZoneState(validDropZone, {
							highlightZone: true,
						});
						lastHighlightedZoneRef.current = validDropZone;
					}
				}
				return;
			}

			// released drag
			if (last) {
				// animation target for after drag (default is the menu item where drag started)
				let target: { top: number; left: number } | undefined = getCenterOfElement(
					event?.target as unknown as HTMLImageElement,
				);

				// check if dropped outside of the list panel
				const panelEl = panelRef.current?.getSymbolSelectionPanelEl();
				const isOutsidePanel = panelEl
					? !document.elementsFromPoint(x, y).includes(panelEl)
					: false;

				const newSymbol = symbols?.find((sym) => sym.code === symbol.code);

				const currentHoveredSymbol = eXpress.core.isSymbolZone(
					lastHighlightedZoneRef?.current,
				)
					? lastHighlightedZoneRef?.current?.symbol
					: undefined;

				// we have a zone hovered
				if (
					lastHighlightedZone &&
					dashboard.canReplaceSymbol(lastHighlightedZone, true) &&
					newSymbol &&
					currentHoveredSymbol !== newSymbol
				) {
					dashboard.replaceSymbol(lastHighlightedZone, newSymbol, true);

					target = getCenterOfZone(lastHighlightedZone);
				} else if (dashboard.canAddSymbolZone(undefined) && newSymbol && isOutsidePanel) {
					dashboard.addSymbolZone({
						symbol: newSymbol,
					});
					// not possible to create a symbol zone at a given point at this time
					target = undefined;
				}

				// remove highlight
				previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
					highlightZone: false,
				});
				lastHighlightedZoneRef.current = undefined;

				// animate to target
				if (target) {
					dragImageRef?.current?.end(target.left - x, target.top - y);
				} else {
					dragImageRef?.current?.end();
				}
			}
		},
		[
			dragImageRef,
			lastHighlightedZoneRef,
			previewOverlayRef,
			dashboard,
			symbols,
			getZonesAtPoint,
			getCenterOfZone,
			onSymbolClick,
		],
	);

	const mappedSymbols = useMemo<IPanelSymbol[]>(() => {
		if (!symbols) return [];
		return symbols
			.map((symbol) => ({
				code: symbol.code,
				url: symbol.thumbUrl?.getAbsoluteUrl(client),
				isWhite: symbol.isWhite,
			}))
			.filter((symbol): symbol is IPanelSymbol => !!symbol.url);
	}, [client, symbols]);

	// Scroll into view if selection is changed (animated)
	useEffect(() => {
		// scroll selection into view if a new symbol is selected
		if (panelRef.current && currentSymbol)
			panelRef.current.scrollIntoView(currentSymbol.code, true);
	}, [currentSymbol]);

	// Scroll into view if symbols list is loaded (not animated)
	useEffect(() => {
		if (panelRef.current && currentSymbol) {
			// This will override the animation
			panelRef.current.scrollIntoView(currentSymbol.code, false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!symbols) return null;

	return (
		<>
			<DragImage ref={dragImageRef} />
			<SymbolsListPanel
				selectedSymbolCode={currentSymbol?.code}
				symbols={mappedSymbols}
				ref={panelRef}
				onClick={onSymbolClick}
				onDrag={onDrag}
			/>
		</>
	);
}
